import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, type FavoriteDTO } from '../lib/api.ts';
import { useFilterStore } from '../stores/filter-store.ts';
import { Icon } from './Icon.tsx';

export function FavoritesPanel() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const filters = useFilterStore((s) => s.filters);
  const setAll = useFilterStore((s) => s.setAll);
  const [showSave, setShowSave] = useState(false);
  const [newName, setNewName] = useState('');

  const { data: favs = [] } = useQuery({
    queryKey: ['favorites'],
    queryFn: api.favoritesList,
  });

  const createMut = useMutation({
    mutationFn: api.favoritesCreate,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['favorites'] });
      setShowSave(false);
      setNewName('');
    },
  });
  const deleteMut = useMutation({
    mutationFn: api.favoritesDelete,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['favorites'] }),
  });
  const pinMut = useMutation({
    mutationFn: ({ id, pinned }: { id: number; pinned: boolean }) =>
      api.favoritesUpdate(id, { pinned }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['favorites'] }),
  });

  function loadFavorite(f: FavoriteDTO) {
    setAll(f.filters as never);
    if (f.path && f.path !== window.location.pathname) {
      navigate(f.path);
    }
  }

  function saveFavorite() {
    if (!newName.trim()) return;
    createMut.mutate({
      name: newName.trim(),
      filters,
      path: window.location.pathname.replace(/^\/statistik/, '') || '/',
    });
  }

  const hasActiveFilters = Object.keys(filters).length > 0;
  const pinned = favs.filter((f) => f.pinned);
  const others = favs.filter((f) => !f.pinned);

  return (
    <div className="border-t border-slate-200 dark:border-slate-800 shrink-0">
      <div className="px-3 py-2 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon name="bookmark" size={14} className="text-slate-500" />
          <span className="font-mono text-2xs uppercase tracking-[0.12em] font-semibold text-slate-500">
            Favoriten
          </span>
          {favs.length > 0 && <span className="badge">{favs.length}</span>}
        </div>
        <button
          onClick={() => setShowSave((s) => !s)}
          disabled={!hasActiveFilters}
          className="btn-icon w-6 h-6 text-slate-400 hover:text-brand-600 disabled:opacity-30 disabled:cursor-not-allowed"
          title={hasActiveFilters ? 'Aktuellen Filter speichern' : 'Setze erst einen Filter'}
        >
          <Icon name="star" size={13} />
        </button>
      </div>

      {showSave && (
        <div className="px-3 pb-2 animate-slide-up">
          <div className="flex gap-1">
            <input
              autoFocus
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') saveFavorite();
                if (e.key === 'Escape') setShowSave(false);
              }}
              placeholder="Name z.B. KW21 Filiale 57"
              className="input text-xs"
            />
            <button
              onClick={saveFavorite}
              disabled={!newName.trim() || createMut.isPending}
              className="btn-primary text-xs px-2"
            >
              <Icon name="check" size={12} />
            </button>
          </div>
        </div>
      )}

      <div className="px-2 pb-3 space-y-0.5 max-h-60 overflow-y-auto scrollbar-thin">
        {pinned.length > 0 && (
          <>
            {pinned.map((f) => (
              <FavoriteRow
                key={f.id}
                fav={f}
                onLoad={() => loadFavorite(f)}
                onDelete={() => deleteMut.mutate(f.id)}
                onTogglePin={() => pinMut.mutate({ id: f.id, pinned: !f.pinned })}
              />
            ))}
            {others.length > 0 && (
              <div className="border-t border-slate-100 dark:border-slate-800 my-1" />
            )}
          </>
        )}
        {others.slice(0, 10).map((f) => (
          <FavoriteRow
            key={f.id}
            fav={f}
            onLoad={() => loadFavorite(f)}
            onDelete={() => deleteMut.mutate(f.id)}
            onTogglePin={() => pinMut.mutate({ id: f.id, pinned: !f.pinned })}
          />
        ))}
        {favs.length === 0 && (
          <div className="px-2 py-3 text-2xs text-slate-400 dark:text-slate-500 italic">
            Setze einen Filter und klicke ★ um zu speichern.
          </div>
        )}
      </div>
    </div>
  );
}

function FavoriteRow({
  fav,
  onLoad,
  onDelete,
  onTogglePin,
}: {
  fav: FavoriteDTO;
  onLoad: () => void;
  onDelete: () => void;
  onTogglePin: () => void;
}) {
  const filterCount = Object.keys(fav.filters).length;
  return (
    <div className="group flex items-center gap-1 px-1.5 py-1 rounded-md hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors">
      <button
        onClick={onTogglePin}
        className={`w-5 h-5 flex items-center justify-center rounded transition-colors ${
          fav.pinned
            ? 'text-warning-500'
            : 'text-slate-300 opacity-0 group-hover:opacity-100 hover:text-warning-500'
        }`}
        title={fav.pinned ? 'Pin entfernen' : 'Pinnen'}
      >
        <Icon name={fav.pinned ? 'star-filled' : 'star'} size={11} />
      </button>
      <button
        onClick={onLoad}
        className="flex-1 text-left truncate text-xs text-slate-700 dark:text-slate-300 hover:text-brand-600 transition-colors"
        title={`${fav.name} (${filterCount} Filter)`}
      >
        {fav.name}
      </button>
      <span className="text-2xs text-slate-400 tabular-nums">{filterCount}</span>
      <button
        onClick={onDelete}
        className="w-5 h-5 flex items-center justify-center text-slate-300 opacity-0 group-hover:opacity-100 hover:text-accent-600 transition-all"
        title="Löschen"
      >
        <Icon name="x" size={11} />
      </button>
    </div>
  );
}
